import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../context/notificationContext";
import { useLanguage } from "../../context/languageContext";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as EventAPI from "../../api/event";
import * as ReportTypeAPI from "../../api/reportType";
import * as ReportAPI from "../../api/report";
import { Field, FormReturnData } from "../../types/FromComponent.types";
import { FormComponent } from "../../component/form/FormComponent";
import { Button } from "../../component/form/Button";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import "./EventReportCreateScreen.css";

type ReportTypeItem = {
    id: number;
    name: string;
};

type ReportFieldItem = {
    id: number;
    label: string;
    field_type: string;
    is_required?: boolean;
};

const toReportType = (item: Record<string, unknown>): ReportTypeItem => ({
    id: Number(item.id ?? item.report_type_id ?? 0),
    name: String(item.name ?? item.report_type_name ?? ""),
});

const toReportField = (item: Record<string, unknown>): ReportFieldItem => ({
    id: Number(item.id ?? item.field_id ?? 0),
    label: String(item.label ?? item.field_label ?? ""),
    field_type: String(item.field_type ?? item.type ?? "text"),
    is_required: Boolean(item.is_required ?? item.required ?? false),
});

const formatDateOnly = (date: Date) => date.toISOString().split("T")[0];

const isDateInsideEvent = (date: Date, startDate: string | Date, endDate: string | Date) => {
    const checkDate = formatDateOnly(date);
    const start = formatDateOnly(new Date(startDate));
    const end = formatDateOnly(new Date(endDate));
    return checkDate >= start && checkDate <= end;
};

const mapDraftValuesFromDetail = (source: Record<string, unknown>): Record<number, string> => {
    const values = source.values;
    if (Array.isArray(values)) {
        return values.reduce((acc, item) => {
            const valueItem = item as Record<string, unknown>;
            const fieldId = valueItem.report_type_field_id as number;
            if (!fieldId) return acc;
            acc[fieldId] = String(valueItem.value ?? "");
            return acc;
        }, {} as Record<number, string>);
    }

    return {};
};

const EventReportCreateScreen: React.FC = () => {
    const [showStartModal, setShowStartModal] = useState(false);
    const [creatingReport, setCreatingReport] = useState(false);
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    const eventId = Number(params.get("event_id") ?? 0);
    const queryDay = params.get("day") ?? "";
    const queryReportTypeId = Number(params.get("report_type_id") ?? 0);
    const queryReportId = Number(params.get("report_id") ?? 0);

    const { association, user } = useAuth();
    const { addNotification } = useNotification();
    const { t } = useLanguage();
    const nav = useNavigate();

    const associationId = String(association.id);
    const today = useMemo(() => formatDateOnly(new Date()), []);
    const requestedReportDay = queryDay || today;

    const [eventName, setEventName] = useState("");
    const [eventStartDate, setEventStartDate] = useState("");
    const [eventEndDate, setEventEndDate] = useState("");
    const [selectedReportTypeId, setSelectedReportTypeId] = useState<number>(queryReportTypeId > 0 ? queryReportTypeId : 0);
    const [reportFieldsMeta, setReportFieldsMeta] = useState<ReportFieldItem[]>([]);
    const [activeReportId, setActiveReportId] = useState<number>(queryReportId > 0 ? queryReportId : 0);
    const [draftValuesByFieldId, setDraftValuesByFieldId] = useState<Record<number, string>>({});
    const [report, setReport] = useState<Record<string, unknown> | null>(null);
    const [formValues, setFormValues] = useState<FormReturnData>({});
    const [screenLoading, setScreenLoading] = useState(true);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadEventAndReportTypes = useCallback(async () => {
        const eventResponse = await EventAPI.detail(eventId);
        if (!isSuccessCode(eventResponse.error_code)) {
            return handleApiError(eventResponse.request_id, eventResponse.error_code);
        }

        const eventData = Array.isArray(eventResponse.data) ? eventResponse.data[0] : eventResponse.data;
        const event = (eventData ?? {}) as Record<string, unknown>;
        setEventName(String(event.title ?? "Event"));
        setEventStartDate(String(event.start_at ?? ""));
        setEventEndDate(String(event.end_at ?? ""));

        const eventReportTypesRaw = event.report_types ?? event.report_type ?? event.reportTypes ?? [];
        const eventReportTypes = (Array.isArray(eventReportTypesRaw) ? eventReportTypesRaw : [])
            .map((item: Record<string, unknown>) => toReportType(item))
            .filter((item) => item.id > 0);

        if (eventReportTypes.length > 0) {
            setSelectedReportTypeId((prev) => prev || eventReportTypes[0].id);
            return;
        }

        const reportTypeResponse = await ReportTypeAPI.list(associationId);
        if (!isSuccessCode(reportTypeResponse.error_code)) {
            return handleApiError(reportTypeResponse.request_id, reportTypeResponse.error_code);
        }

        const mappedTypes = (Array.isArray(reportTypeResponse.data) ? reportTypeResponse.data : [])
            .map((item: Record<string, unknown>) => toReportType(item))
            .filter((item) => item.id > 0);
        setSelectedReportTypeId((prev) => prev || mappedTypes[0]?.id || 0);
    }, [associationId, eventId, handleApiError]);

    const loadSelectedReportTypeFields = useCallback(async () => {
        if (!selectedReportTypeId) {
            setReportFieldsMeta([]);
            return;
        }

        const response = await ReportTypeAPI.detail(selectedReportTypeId);
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }

        const detailSource = response.data;
        const detail = (detailSource ?? {}) as Record<string, unknown>;
        let fieldsSource = detail.fields;
        // Se non ci sono fields, prova a estrarre da values (come da esempio output)
        if (!fieldsSource && Array.isArray(detail.values)) {
            fieldsSource = detail.values.map((value) => {
                const row = value as Record<string, unknown>;
                return {
                id: Number(row.report_type_field_id ?? row.id ?? 0),
                label: String(row.label ?? ""),
                field_type: String(row.field_type ?? "text"),
                is_required: false // non c'è info, default false
                };
            });
        }
        const fields = (Array.isArray(fieldsSource) ? fieldsSource : [])
            .map((field: Record<string, unknown>) => toReportField(field))
            .filter((field) => field.id > 0);
        setReportFieldsMeta(fields);
    }, [handleApiError, selectedReportTypeId]);

    const loadDraftValues = useCallback(async (reportId: number) => {
        console.log(reportId);
        
        if (!reportId) {
            setDraftValuesByFieldId({});
            setReport(null);
            return;
        }
        const response = await ReportAPI.detail(reportId);
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }
        const source = Array.isArray(response.data) ? response.data[0] : response.data;
        const reportDetail = (source ?? {}) as Record<string, unknown>;
        const reportType = (reportDetail.report_type ?? {}) as Record<string, unknown>;
        const reportTypeId = Number(reportDetail.report_type_id ?? reportType.id ?? 0);
        if (reportTypeId > 0) {
            setSelectedReportTypeId(reportTypeId);
        }
        setReport(reportDetail);
        setDraftValuesByFieldId(mapDraftValuesFromDetail(reportDetail));
    }, [handleApiError]);

    useEffect(() => {
        if (!eventId) {
            addNotification(t("reportCreate.invalidEventTitle"), t("reportCreate.invalidEventDescription"), "error");
            nav("/events");
            return;
        }

        setScreenLoading(true);
        loadEventAndReportTypes()
            .then(() => {
                if (queryReportId > 0) {
                    setActiveReportId(queryReportId);
                    return;
                }
                setShowStartModal(true);
            })
            .finally(() => setScreenLoading(false));
    }, [addNotification, eventId, loadEventAndReportTypes, nav, queryReportId, t]);

    useEffect(() => {
        setScreenLoading(true);
        loadSelectedReportTypeFields().finally(() => setScreenLoading(false));
    }, [loadSelectedReportTypeFields]);

    useEffect(() => {
        if (activeReportId > 0 && showStartModal) {
            setShowStartModal(false);
        }
    }, [activeReportId, showStartModal]);

    const handleStartReport = async () => {
        setCreatingReport(true);
        const createResponse = await ReportAPI.create({
            event_id: eventId,
            report_type_id: selectedReportTypeId,
            athlete_id: user.id,
            day: requestedReportDay
        });
        setCreatingReport(false);
        setShowStartModal(false);
        if (!isSuccessCode(createResponse.error_code)) {
            handleApiError(createResponse.request_id, createResponse.error_code);
            return;
        }
        const source = Array.isArray(createResponse.data) ? createResponse.data[0] : createResponse.data;
        const reportData = (source ?? {}) as Record<string, unknown>;
        const reportId = Number(reportData.id ?? reportData.report_id ?? 0);
        if (!reportId) {
            addNotification(t("reportCreate.errorTitle"), t("reportCreate.draftIdMissing"), "error");
            return;
        }
        setActiveReportId(reportId);
        setReport({ ...reportData, report_day: String(reportData.report_day ?? requestedReportDay) });
        setDraftValuesByFieldId({});
    };

    useEffect(() => {
        setScreenLoading(true);
        loadDraftValues(activeReportId).finally(() => setScreenLoading(false));
    }, [activeReportId, loadDraftValues]);

    const reportFields: Field[] = useMemo(() => reportFieldsMeta.map((field) => {
        if (field.field_type === "boolean") {
            return {
                name: `field_${field.id}`,
                placeholder: field.label,
                type: "dropdown",
                df: draftValuesByFieldId[field.id],
                options: [{ id: "true", value: "True" }, { id: "false", value: "False" }],
                optional: true,
            };
        }

        if (field.field_type === "date") {
            return {
                name: `field_${field.id}`,
                placeholder: field.label,
                type: "date",
                df: draftValuesByFieldId[field.id],
                optional: true,
            };
        }

        return {
            name: `field_${field.id}`,
            placeholder: field.label,
            type: field.field_type as Field["type"] || "text",
            df: draftValuesByFieldId[field.id],
            optional: true,
        };
    }), [draftValuesByFieldId, reportFieldsMeta]);

    const isEventDayOpen = eventStartDate && eventEndDate
        ? isDateInsideEvent(new Date(), eventStartDate, eventEndDate)
        : false;

    const saveReport = useCallback(async (data: FormReturnData, withNotification = true): Promise<number | null> => {
        if (!selectedReportTypeId) {
            addNotification(t("reportCreate.validationTitle"), t("reportCreate.reportTypeRequired"), "error");
            return null;
        }

        let reportId = activeReportId;
        if (!reportId) {
            const createResponse = await ReportAPI.create({
                event_id: eventId,
                report_type_id: selectedReportTypeId,
                athlete_id: user.id,
                day: requestedReportDay,
            });
            if (!isSuccessCode(createResponse.error_code)) {
                handleApiError(createResponse.request_id, createResponse.error_code);
                return null;
            }

            const source = Array.isArray(createResponse.data) ? createResponse.data[0] : createResponse.data;
            const reportData = (source ?? {}) as Record<string, unknown>;
            reportId = Number(reportData.id ?? reportData.report_id ?? 0);
            if (!reportId) {
                addNotification(t("reportCreate.errorTitle"), t("reportCreate.draftIdMissing"), "error");
                return null;
            }
            setActiveReportId(reportId);
            setReport({ ...reportData, report_day: String(reportData.report_day ?? requestedReportDay) });
        }

        const values = reportFieldsMeta.map((field) => ({
            field_id: field.id,
            value: String(data[`field_${field.id}`] ?? ""),
        }));
        const updateResponse = await ReportAPI.update({ report_id: reportId, values });
        if (!isSuccessCode(updateResponse.error_code)) {
            handleApiError(updateResponse.request_id, updateResponse.error_code);
            return null;
        }

        if (withNotification) {
            addNotification(t("reportCreate.savedTitle"), t("reportCreate.savedDescription"), "success");
        }
        return reportId;
    }, [
        activeReportId,
        addNotification,
        eventId,
        handleApiError,
        reportFieldsMeta,
        requestedReportDay,
        selectedReportTypeId,
        t,
        user.id
    ]);

    const handleSaveReport = async (data: FormReturnData) => {
        setFormValues(data);
        setScreenLoading(true);
        saveReport(data, true).then(() => loadDraftValues(activeReportId).then(() => {
            setScreenLoading(false);
        }));
    };

    const handleSubmitReport = async () => {
        setScreenLoading(true);
        const reportId = await saveReport(formValues, false);
        if (!reportId) {
            setScreenLoading(false);
            return;
        }

        const submitResponse = await ReportAPI.submit(reportId);
        if (!isSuccessCode(submitResponse.error_code)) {
            setScreenLoading(false);
            return handleApiError(submitResponse.request_id, submitResponse.error_code);
        }

        addNotification(t("reportCreate.submittedTitle"), t("reportCreate.submittedDescription"), "success");
        setScreenLoading(false);
        nav(`/event/view/?event_id=${eventId}`);
    };

    if (screenLoading) {
        return <LoadingScreenLocal />;
    }

    if (!isEventDayOpen) {
        return (
            <div className="event-report-create-screen">
                <h2>{eventName || t("reportCreate.eventLabel")}</h2>
                <p>{t("reportCreate.outsideEventWindow")}</p>
                <Button name={t("reportCreate.backToEvent")} action={() => nav(`/event/view/?event_id=${eventId}`)} color="red" />
            </div>
        );
    }

    return (
        <div className="event-report-create-screen">
            <p>{t("reportCreate.eventLabel")}: {eventName}</p>
            <p>{t("reportCreate.reportDateLabel")}: {String(report?.event_day ?? requestedReportDay)}</p>
            {showStartModal && activeReportId === 0 && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{t("reportCreate.startPrompt")}</h3>
                        <Button name={creatingReport ? t("reportCreate.starting") : t("reportCreate.startYes")} action={handleStartReport} color="green" disabled={creatingReport} />
                        <Button name={t("reportCreate.cancel")} action={() => { setShowStartModal(false); nav(`/event/view/?event_id=${eventId}`); }} color="red" />
                    </div>
                </div>
            )}

            {!showStartModal && activeReportId > 0 && (
                <>
                    <div role="status" aria-live="polite">
                        <p>{queryReportId > 0 ? t("reportCreate.loadedFromUrl") : t("reportCreate.newReportCreated")}</p>
                    </div>
                    {selectedReportTypeId > 0 && reportFields.length > 0 && (
                        <>
                            <FormComponent
                                title={t("reportCreate.fillFields")}
                                fields={reportFields}
                                onSubmit={handleSaveReport}
                                onChange={setFormValues}
                                button={{ name: t("reportCreate.save") }}
                            />
                            <Button name={t("reportCreate.send")} action={handleSubmitReport} />
                        </>
                    )}
                    {selectedReportTypeId > 0 && reportFields.length === 0 && (
                        <p>{t("reportCreate.noFieldsConfigured")}</p>
                    )}
                </>
            )}
        </div>
    );
};

export default EventReportCreateScreen;
